import React from 'react';

import ArrowBack from '@mui/icons-material/ArrowBack';
import ArrowForward from '@mui/icons-material/ArrowForward';
import { IconButton, useMediaQuery, useTheme } from '@mui/material';
import { Theme } from '@mui/material/styles';
import { DisplayFilters } from 'src/utils/filters/display-filters';
import DietaryTemplateFilterChips from './DietaryTemplateFilterChips';

const ScrollableChipRow: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const theme = useTheme();

  const isSmallScreen = useMediaQuery<Theme>((theme) =>
    theme.breakpoints.down('md'),
  );

  const [canScrollRight, setCanScrollRight] = React.useState(false);
  const [canScrollLeft, setCanScrollLeft] = React.useState(false);

  const scrollRef = React.useRef<HTMLDivElement | null>(null);

  const getScrollAvailability = (el: HTMLDivElement) => {
    const hasOverflowX = el.scrollWidth - el.clientWidth > 1;
    const atStart = el.scrollLeft <= 1;
    const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 1;

    return {
      canScrollLeft: hasOverflowX && !atStart,
      canScrollRight: hasOverflowX && !atEnd,
    };
  };

  const computeChipOverflowState = React.useCallback(() => {
    const el = scrollRef.current;
    if (!el) {
      return;
    }

    const { canScrollLeft, canScrollRight } = getScrollAvailability(el);
    setCanScrollLeft(canScrollLeft);
    setCanScrollRight(canScrollRight);
  }, []);

  const handleScroll = React.useCallback(() => {
    const el = scrollRef.current;
    if (!el) {
      return;
    }

    const { canScrollLeft, canScrollRight } = getScrollAvailability(el);
    setCanScrollLeft(canScrollLeft);
    setCanScrollRight(canScrollRight);
  }, []);

  React.useEffect(() => {
    computeChipOverflowState();

    const el = scrollRef.current;
    if (!el) {
      return;
    }

    const containerResizeObserver = new ResizeObserver(
      computeChipOverflowState,
    );
    containerResizeObserver.observe(el);

    const childrenResizeObserver = new ResizeObserver(computeChipOverflowState);
    Array.from(el.children).forEach((c) =>
      childrenResizeObserver.observe(c as Element),
    );

    const childrenMutationObserver = new MutationObserver(() => {
      computeChipOverflowState();
      childrenResizeObserver.disconnect();
      Array.from(el.children).forEach((c) =>
        childrenResizeObserver.observe(c as Element),
      );
    });
    childrenMutationObserver.observe(el, { childList: true });

    const onWinResize = () => computeChipOverflowState();
    window.addEventListener('resize', onWinResize);

    return () => {
      containerResizeObserver.disconnect();
      childrenResizeObserver.disconnect();
      childrenMutationObserver.disconnect();
      window.removeEventListener('resize', onWinResize);
    };
  }, [computeChipOverflowState]);

  return (
    <div
      style={{
        height: 56,
        position: 'relative',
        overflowX: 'hidden',
        display: 'flex',
      }}
    >
      {!isSmallScreen && (
        <div
          css={{
            position: 'absolute',
            padding: '0 8px',
            display: 'flex',
            alignItems: 'center',
            width: '100%',
            height: '100%',
          }}
        >
          {canScrollLeft && (
            <IconButton
              disableRipple
              onClick={() =>
                scrollRef.current?.scrollBy({ left: -240, behavior: 'smooth' })
              }
              css={{
                background: theme.palette.background.default,
                border: 'solid 1px',
                borderColor: '#D3D3D3',
                height: 40,
                width: 40,
                zIndex: 1000,
              }}
            >
              <ArrowBack css={{ color: theme.palette.text.secondary }} />
            </IconButton>
          )}
          {canScrollRight && (
            <IconButton
              disableRipple
              onClick={() =>
                scrollRef.current?.scrollBy({ left: 240, behavior: 'smooth' })
              }
              css={{
                background: theme.palette.background.default,
                border: 'solid 1px',
                borderColor: '#D3D3D3',
                height: 40,
                width: 40,
                marginLeft: 'auto',
                zIndex: 1000,
              }}
            >
              <ArrowForward css={{ color: theme.palette.text.secondary }} />
            </IconButton>
          )}
        </div>
      )}

      <div
        ref={scrollRef}
        onScroll={handleScroll}
        css={{
          overflowX: 'scroll',
          display: 'flex',
          alignItems: 'center',
          '&:before, &:after': { content: '""', margin: 'auto' },
          scrollBarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
          padding: '0 0.10rem',
          height: 'auto',
        }}
      >
        {children}
      </div>
    </div>
  );
};

interface Props {
  displayFilters: DisplayFilters;
  selectedFilters: DisplayFilters;
  setSelectedFilters: React.Dispatch<React.SetStateAction<DisplayFilters>>;
}

const ScrollableOneClickFilterPanel: React.FC<Props> = ({
  displayFilters,
  selectedFilters,
  setSelectedFilters,
}) => {
  return (
    <ScrollableChipRow>
      <DietaryTemplateFilterChips
        displayFilters={displayFilters}
        selectedFilters={selectedFilters}
        setSelectedFilters={setSelectedFilters}
      />
    </ScrollableChipRow>
  );
};

export default ScrollableOneClickFilterPanel;
